// Private-mode-safe Storage access.
//
// Safari (and some embedded webviews) in private/locked-down modes expose a
// `sessionStorage`/`localStorage` object whose `setItem` throws synchronously
// (QuotaExceededError) — or whose mere property access throws. Reading these at
// module-eval time (Vuex/Pinia persistence plugins do exactly this) white-screens
// the app on init. This module probes each native Storage with an actual
// write/remove and falls back to a conforming in-memory object when unusable.
//
// Used at module-eval time (store/index.js, main.js) and from Options-API code,
// so this is plain JS — no Vue composable, no reactivity.

// Method-surface only: implements getItem/setItem/removeItem/clear/key/length.
// It intentionally does NOT support the DOM Storage index signature
// (`storage.foo` / `storage[0]`) — neither vuex-persist nor
// pinia-plugin-persistedstate v3 use it. Keys are String()-coerced symmetrically
// on get/set/remove to match native Storage.
// @returns {Storage}
function createMemoryStorage() {
  const map = new Map();
  return {
    getItem: (k) => { const kk = String(k); return map.has(kk) ? map.get(kk) : null; },
    setItem: (k, v) => { map.set(String(k), String(v)); },
    removeItem: (k) => { map.delete(String(k)); },
    clear: () => { map.clear(); },
    key: (i) => Array.from(map.keys())[i] ?? null,
    get length() { return map.size; },
  };
}

function isStorageUsable(storage) {
  try {
    const probe = '__az_probe__';
    storage.setItem(probe, '1'); // private mode throws HERE, not on access
    storage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

// getNative is a thunk because accessing window.sessionStorage can itself throw.
export function createSafeStorage(getNative) {
  let native = null;
  try { native = getNative(); } catch { native = null; }
  if (native && isStorageUsable(native)) return native;
  console.warn('[safeStorage] persistent storage unavailable; using in-memory fallback');
  return createMemoryStorage();
}

// Resolved once at import. Safe to reference at module-eval time.
export const safeSessionStorage = createSafeStorage(() => window.sessionStorage);
export const safeLocalStorage = createSafeStorage(() => window.localStorage);
